import { useEffect, useState } from 'react';
import { fetchWithFallback } from '@/modules/brain/services/company.api';

/**
 * Read-with-fallback hook for the Brain → Company pages.
 *
 * The demo always renders something — if the API returns null (no row
 * yet) or errors, the seed fallback is shown so the screen never goes
 * blank on stage. `fromApi` lets callers show a "live" badge if you
 * ever want to expose the distinction.
 */
export function useCompanyResource<T>(
    fetcher: () => Promise<T | null>,
    fallback: T,
    label: string,
): {
    value: T;
    fromApi: boolean;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
} {
    const [value, setValue] = useState<T>(fallback);
    const [fromApi, setFromApi] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const result = await fetchWithFallback(fetcher, fallback, label);
            setValue(result.value);
            setFromApi(result.fromApi);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { value, fromApi, loading, error, refresh };
}
